import type { components } from './schema';

/**
 * The single HTTP client.
 *
 * Every request in the application goes through here, which is what makes the cross-cutting
 * concerns actually consistent: the bearer token, the correlation id, the error envelope, the
 * timeout, and the 401 handling all exist in exactly one place.
 */

/** The backend's error envelope. Mirrors `ApiError` on the server. */
export interface ApiErrorBody {
  code: string;
  message: string;
  requestId?: string | null;
  timestamp?: string;
  details?: Record<string, unknown> | null;
  fieldErrors?: { field: string; message: string }[] | null;
}

/**
 * A failed request, normalised.
 *
 * Carries `requestId` because that is what support needs to find the request in the server logs
 * and the audit trail - it is shown to the user on every error surface rather than buried.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  readonly fieldErrors: { field: string; message: string }[];
  readonly details: Record<string, unknown> | null;

  constructor(status: number, body: Partial<ApiErrorBody>, fallbackMessage: string) {
    super(body.message ?? fallbackMessage);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code ?? inferCode(status);
    this.requestId = body.requestId ?? null;
    this.fieldErrors = body.fieldErrors ?? [];
    this.details = body.details ?? null;
  }

  /** Retrying a 5xx or a network failure can help; retrying a 4xx cannot. */
  get isRetryable(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500;
  }

  get isAuthError(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** 409 means the resource changed underneath the user, or a business rule refused the action. */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

function inferCode(status: number): string {
  switch (status) {
    case 0:
      return 'NETWORK_UNAVAILABLE';
    case 401:
      return 'UNAUTHENTICATED';
    case 403:
      return 'ACCESS_DENIED';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'CONFLICT';
    case 429:
      return 'RATE_LIMITED';
    case 504:
      return 'TIMEOUT';
    default:
      return status >= 500 ? 'INTERNAL_ERROR' : 'REQUEST_FAILED';
  }
}

// ---- token handling ---------------------------------------------------------------------------

/**
 * The access token is held in memory, mirrored into localStorage for browser profile-wide
 * persistence across tabs and windows.
 */
const TOKEN_STORAGE_KEY = 'dam.accessToken';

function readStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY) ?? sessionStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

let accessToken: string | null = readStoredToken();
let onUnauthenticated: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
  try {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem('dam.sessionStart');
      localStorage.removeItem('dam.lastActivity');
      localStorage.removeItem('dam.idleTimeoutMinutes');
      localStorage.removeItem('dam.maxSessionMinutes');
    }
    // Clean up any legacy sessionStorage copy
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // Private-mode or blocked storage: the in-memory token still functions for the tab.
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Registered by the auth provider so a 401 anywhere ends the session exactly once. */
export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

// ---- request ----------------------------------------------------------------------------------

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
const DEFAULT_TIMEOUT_MS = 30_000;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Serialised as JSON. Mutually exclusive with `formData`. */
  body?: unknown;
  /** Sent as multipart/form-data, with no Content-Type so the browser sets the boundary. */
  formData?: FormData;
  query?: Record<string, unknown>;
  signal?: AbortSignal;
  timeoutMs?: number;
  /**
   * Sent as `Idempotency-Key`. Required by endpoints that create business state, so a retry after
   * a timeout cannot produce a second record.
   */
  idempotencyKey?: string;
}

function buildQuery(query: Record<string, unknown> | undefined): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry !== undefined && entry !== null && entry !== '') params.append(key, String(entry));
      });
    } else {
      params.append(key, String(value));
    }
  }
  const serialised = params.toString();
  return serialised ? `?${serialised}` : '';
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, formData, query, signal, timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  // A delayed response belongs to the credentials used when it was sent, not necessarily the
  // session that is active when it arrives.
  const requestAccessToken = accessToken;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (requestAccessToken) headers.Authorization = `Bearer ${requestAccessToken}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;
  // Ties several requests belonging to one user action together in the server logs.
  headers['X-Correlation-Id'] = correlationId();

  // A request with no timeout hangs forever on a black-holed connection, and the user sees a
  // spinner that never resolves.
  const timeoutController = new AbortController();
  const timeout = setTimeout(() => timeoutController.abort(), timeoutMs);
  const composedSignal = signal
    ? AbortSignal.any([signal, timeoutController.signal])
    : timeoutController.signal;

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}${buildQuery(query)}`, {
      method,
      headers,
      body: formData ?? (body !== undefined ? JSON.stringify(body) : undefined),
      signal: composedSignal,
      credentials: 'same-origin',
    });
  } catch (cause) {
    clearTimeout(timeout);
    if (signal?.aborted) throw cause; // A deliberate cancellation, not a failure.
    const timedOut = timeoutController.signal.aborted;
    throw new ApiError(
      0,
      {
        code: timedOut ? 'TIMEOUT' : 'NETWORK_UNAVAILABLE',
        message: timedOut
          ? 'The server took too long to respond.'
          : 'Cannot reach the server. Check your connection.',
      },
      'Network request failed',
    );
  } finally {
    clearTimeout(timeout);
  }

  if (response.status === 401 && requestAccessToken && accessToken === requestAccessToken) {
    onUnauthenticated?.();
  }

  if (!response.ok) {
    let parsed: Partial<ApiErrorBody> = {};
    try {
      parsed = (await response.json()) as Partial<ApiErrorBody>;
    } catch {
      // A non-JSON error body (a proxy error page, for example). The status still tells us enough.
    }
    // The request id is also on the response header, which is the only place it exists when the
    // failure happened before the application produced a body.
    const headerRequestId = response.headers.get('X-Request-Id');
    if (!parsed.requestId && headerRequestId) parsed.requestId = headerRequestId;
    throw new ApiError(response.status, parsed, response.statusText || 'Request failed');
  }

  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined as T;
  }

  const contentType = response.headers.get('Content-Type') ?? '';
  if (!contentType.includes('json')) {
    return (await response.text()) as unknown as T;
  }
  return (await response.json()) as T;
}

function correlationId(): string {
  // Constrained to what the server accepts: [A-Za-z0-9._:-]{1,64}. A value outside that is
  // replaced server-side, which would silently break correlation.
  return crypto.randomUUID();
}

/** Convenience wrappers, so call sites read as the operation rather than as HTTP. */
export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  postForm: <T>(path: string, formData: FormData, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'POST', formData }),
  /**
   * Used sparingly. Almost nothing in this system is deleted - records are disabled, withdrawn or
   * superseded, because the history is usually the point. Where DELETE does appear, the server
   * side is a state change rather than a removal.
   */
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

/**
 * Fetches a protected binary resource as an object URL.
 *
 * An `<img src="/api/...">` cannot be used for anything the server protects: the browser issues
 * that request without the Authorization header, so it comes back 401 and the element renders a
 * broken image. Fetching the bytes through the authenticated client and wrapping them in an object
 * URL is what makes a protected image displayable at all.
 *
 * The caller owns the returned URL and must revoke it when the element goes away, or every avatar
 * ever rendered stays in memory for the life of the tab.
 */
export async function fetchObjectUrl(path: string, signal?: AbortSignal): Promise<string> {
  const response = await fetchBinary(path, { signal }, 'The image could not be loaded');
  return URL.createObjectURL(await response.blob());
}

/**
 * Posts a JSON body and saves the file the server returns, under the name it gives.
 *
 * For generated downloads the browser cannot fetch by itself - the request needs the bearer token
 * and, often, a body. A refusal is raised as an ApiError carrying the server's message.
 */
export async function downloadFile(path: string, body: unknown, fallbackName: string): Promise<void> {
  const response = await fetchBinary(
    path,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    'The download failed',
  );
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const name = /filename="?([^";]+)"?/i.exec(disposition)?.[1] ?? fallbackName;

  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked once the browser has taken the file; revoking at once can cancel the download.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Downloads an authenticated file over GET, preserving the server-provided filename. */
function downloadName(disposition: string, fallback: string): string {
  const safe = (name: string) => name && !/[\/\\\u0000-\u001f\u007f]/.test(name);
  const extended = /(?:^|;)\s*filename\*\s*=\s*(?:"([^"]*)"|([^;]*))/i.exec(disposition);
  if (extended) {
    const encoded = /^(?:UTF-8)'[^']*'(.*)$/i.exec((extended[1] ?? extended[2] ?? '').trim());
    if (encoded) {
      try {
        const name = decodeURIComponent(encoded[1]!);
        if (safe(name)) return name;
      } catch { /* A malformed extended filename falls back to the ordinary header/name. */ }
    }
  }
  const ordinary = /(?:^|;)\s*filename\s*=\s*(?:"([^"]+)"|([^";]+))/i.exec(disposition);
  const name = (ordinary?.[1] ?? ordinary?.[2] ?? '').trim();
  return safe(name) ? name : fallback;
}

export async function downloadGetFile(path: string, fallbackName: string): Promise<void> {
  const response = await fetchBinary(path, { method: 'GET' }, 'The download failed');
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const name = downloadName(disposition, fallbackName);
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Fetches authenticated text content for client-generated commands or configuration previews. */
export async function fetchTextFile(path: string): Promise<string> {
  const response = await fetchBinary(path, { method: 'GET' }, 'The file could not be loaded');
  return response.text();
}

/** An authenticated request for binary content, with the server's error body when it refuses. */
async function fetchBinary(path: string, init: RequestInit, fallbackMessage: string): Promise<Response> {
  const requestAccessToken = accessToken;
  const headers: Record<string, string> = {
    Accept: '*/*',
    'X-Correlation-Id': correlationId(),
    ...(init.headers as Record<string, string> | undefined),
  };
  if (requestAccessToken) headers.Authorization = `Bearer ${requestAccessToken}`;

  const response = await fetch(`${BASE_URL}${path}`, { ...init, headers, credentials: 'same-origin' });
  if (response.status === 401 && requestAccessToken && accessToken === requestAccessToken) {
    onUnauthenticated?.();
  }
  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as Partial<ApiErrorBody>;
    throw new ApiError(response.status, errorBody, fallbackMessage);
  }
  return response;
}

/** Shorthand for the generated schema types, so features import one name rather than a path. */
export type Schemas = components['schemas'];
